import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BLOCK_TYPE_PERMANENT } from '@libs/shared/domain/patient-assistance/pharmacy/enrollment-group/shared-patient-assistance-pharmacy-enrollment-group-block-type.type';
import { SharedPatientAssistancePharmacyEnrollmentGroupNetworkTypeType } from '@libs/shared/domain/patient-assistance/pharmacy/enrollment-group/shared-patient-assistance-pharmacy-enrollment-group-network-type.type';
import { SharedPatientAssistancePharmacyEnrollmentGroupChangePropertiesSchema } from '@libs/shared/domain/patient-assistance/pharmacy/enrollment-group/shared-patient-assistance-pharmacy-enrollment-group.change-properties.schema';
import { SharedPatientAssistancePharmacyEnrollmentGroupPropertiesSchema } from '@libs/shared/domain/patient-assistance/pharmacy/enrollment-group/shared-patient-assistance-pharmacy-enrollment-group.properties.schema';
import { SharedPatientAssistancePharmacyEnrollmentGroupVo } from '@libs/shared/domain/patient-assistance/pharmacy/enrollment-group/shared-patient-assistance-pharmacy-enrollment-group.vo';
import { TenantService } from '@services/tenant.service';
import { UrlService } from '@services/url.service';
import { UserService } from '@services/user.service';
import { SelectListItem } from '@shared/models/select-list-item';
import { UserRoleModel } from '@shared/models/user-role';
import { sortBy } from 'lodash';
import { map, Observable } from 'rxjs';

export const NETWORK_GROUP_LABEL_MAP: Record<SharedPatientAssistancePharmacyEnrollmentGroupNetworkTypeType, string> = {
  CLOSED: 'Contracted Network',
  OPEN: 'Open Network',
  OTHER: 'Other',
};

@Injectable({
  providedIn: 'root',
})
export class ProgramGroupService {
  private readonly httpClient = inject(HttpClient);
  private readonly urlService = inject(UrlService);
  private readonly userService = inject(UserService);
  private readonly tenantService = inject(TenantService);

  readonly sortNameIteratee = (programGroup: SharedPatientAssistancePharmacyEnrollmentGroupVo) => programGroup.name.toUpperCase();

  getProgramGroupsGroupedByTenant(): Observable<Record<string, SharedPatientAssistancePharmacyEnrollmentGroupVo[]>> {
    return this.httpClient
      .get<{
        pharmacyEnrollmentGroups: Record<string, SharedPatientAssistancePharmacyEnrollmentGroupVo[]>;
      }>(`${this.urlService.PA_SERVICE}/pharmacy/enrollment-groups-by-tenant`)
      .pipe(map(result => result.pharmacyEnrollmentGroups));
  }

  getProgramGroupsForTenant(tenant: string): Observable<SharedPatientAssistancePharmacyEnrollmentGroupVo[]> {
    return this.httpClient
      .get<{
        pharmacyEnrollmentGroups: SharedPatientAssistancePharmacyEnrollmentGroupVo[];
      }>(`${this.urlService.PA_SERVICE}/pharmacy/enrollment-groups/${tenant}`)
      .pipe(map(result => result.pharmacyEnrollmentGroups));
  }

  getAllProgramGroupsForCurrentUser(): Observable<Record<string, SharedPatientAssistancePharmacyEnrollmentGroupVo[]>> {
    if (!this.userService.hasAllTenantsAccess()) {
      const userTenant = this.userService.getUserTenant() ?? '';
      return this.getProgramGroupsForTenant(userTenant).pipe(map(result => ({ [userTenant]: result })));
    }
    return this.getProgramGroupsGroupedByTenant();
  }

  getSelectListOptions(forTenantId?: string): Observable<SelectListItem<string>[]> {
    const userAccessibleTenants = this.userService
      .getUserAccessibleTenants()
      .filter(tenant => (forTenantId ? tenant.id === forTenantId : true));
    const groupOptionsByTenant = userAccessibleTenants.length > 1;
    return this.getAllProgramGroupsForCurrentUser().pipe(
      map(programGroups => {
        const options: SelectListItem<string>[] = [];
        for (const [tenantId, tenantProgramGroups] of Object.entries(programGroups).sort(([keyA], [keyB]) => keyA.localeCompare(keyB))) {
          if (!!forTenantId && tenantId !== forTenantId) continue;
          const sortedProgramGroups = [...tenantProgramGroups].sort(
            (a, b) => a.networkType.localeCompare(b.networkType) || a.name.localeCompare(b.name),
          );
          let lastNetworkType: string = '';
          for (const programGroup of sortedProgramGroups) {
            let subGroupTitle: string | undefined = undefined;
            if (lastNetworkType !== programGroup.networkType) {
              subGroupTitle = NETWORK_GROUP_LABEL_MAP[programGroup.networkType];
              lastNetworkType = programGroup.networkType;
            }
            options.push({
              name: programGroup.name,
              value: `groupBy:${tenantId}|${programGroup.id}`,
              group: groupOptionsByTenant ? (this.tenantService.getTenantById(tenantId)?.name ?? 'Other') : undefined,
              subGroupTitle,
            });
          }
        }
        return options;
      }),
    );
  }

  createProgramGroup(programGroupData: SharedPatientAssistancePharmacyEnrollmentGroupPropertiesSchema) {
    return this.httpClient
      .post<{
        pharmacyEnrollmentGroup: SharedPatientAssistancePharmacyEnrollmentGroupVo;
      }>(`${this.urlService.PA_SERVICE}/pharmacy/enrollment-groups`, programGroupData)
      .pipe(map(result => result.pharmacyEnrollmentGroup));
  }

  updateProgramGroup(
    tenant: string,
    id: string,
    programGroupData: Omit<SharedPatientAssistancePharmacyEnrollmentGroupChangePropertiesSchema, 'tenant' | 'id'>,
  ) {
    return this.httpClient
      .patch<{
        pharmacyEnrollmentGroup: SharedPatientAssistancePharmacyEnrollmentGroupVo;
      }>(`${this.urlService.PA_SERVICE}/pharmacy/enrollment-groups/${tenant}/${id}`, programGroupData)
      .pipe(map(result => result.pharmacyEnrollmentGroup));
  }

  filterBlockedProgramGroups(
    programGroups: SharedPatientAssistancePharmacyEnrollmentGroupVo[],
    allowPermanentlyBlockedProgramGroups = false,
  ) {
    return programGroups.filter(
      programGroup =>
        !programGroup.isBlocked || (allowPermanentlyBlockedProgramGroups && programGroup.options.blockType === BLOCK_TYPE_PERMANENT),
    );
  }

  filterBlockedProgramGroupsFromMap(
    programGroupsMap: Record<string, SharedPatientAssistancePharmacyEnrollmentGroupVo[]>,
    allowPermanentlyBlockedProgramGroups = false,
  ) {
    const filteredProgramGroupsMap: Record<string, SharedPatientAssistancePharmacyEnrollmentGroupVo[]> = {};
    for (const key of Object.keys(programGroupsMap)) {
      filteredProgramGroupsMap[key] = this.filterBlockedProgramGroups(programGroupsMap[key], allowPermanentlyBlockedProgramGroups);
    }
    return filteredProgramGroupsMap;
  }

  filterBlockedProgramGroupsForDataview(
    programGroupsMap: Record<string, SharedPatientAssistancePharmacyEnrollmentGroupVo[]>,
    filterId: 'pharmacyProgramGroup' | 'programGroupId',
    userRole: UserRoleModel,
  ) {
    if (filterId === 'programGroupId') return this.filterBlockedProgramGroupsFromMap(programGroupsMap);
    if (userRole.rolename === 'ProgramAdmin') return this.filterBlockedProgramGroupsFromMap(programGroupsMap, true);
    return programGroupsMap;
  }

  filterAndSortBlockedProgramGroupsForDataview(
    programGroupsMap: Record<string, SharedPatientAssistancePharmacyEnrollmentGroupVo[]>,
    filterId: 'pharmacyProgramGroup' | 'programGroupId',
    userRole: UserRoleModel,
  ) {
    const filteredAndSortedProgramGroupsMap: Record<string, SharedPatientAssistancePharmacyEnrollmentGroupVo[]> = {};
    for (const [key, value] of Object.entries(this.filterBlockedProgramGroupsForDataview(programGroupsMap, filterId, userRole))) {
      filteredAndSortedProgramGroupsMap[key] = sortBy(value, ['networkType', this.sortNameIteratee]);
    }
    return filteredAndSortedProgramGroupsMap;
  }
}
